import React from "react";
import { Grid, Box, Typography, Button } from "@mui/material";
import DocumentMeta from "react-document-meta";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BlogPostData, commonBlogKeywords } from "../Blog";

interface BreadcrumbItem {
  name: string;
  item: string;
}

interface BlogArticleViewProps {
  post: BlogPostData;
  canonical: string;
  ogType: "website" | "article";
  breadcrumbs: BreadcrumbItem[]; // TOPから記事自身までを順に並べたもの
  backHref: string;
  backLabel: string;
}

// ブログ記事1件分の描画(SEOメタ・構造化データ・本文ブロック)を担う共通コンポーネント。
// 全店舗共通のブログ(BlogPost.tsx)と、店舗別ブログ(RegionBlogPost.tsx)の両方から利用する。
const BlogArticleView = ({
  post,
  canonical,
  breadcrumbs,
  backHref,
  backLabel,
}: BlogArticleViewProps) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const navigate = (path: string) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.location.href = path;
  };

  const seoTitle = post.seo?.title ?? `${post.title} - 土日だけ体操教室ブログ`;
  const seoDescription = post.seo?.description ?? post.excerpt;
  const thumbnailAlt = post.imageAlt ?? post.title;
  // og:image はSNS側がそのまま参照するため絶対URLにする(相対パスは不可)
  const absoluteImage = post.image.startsWith("http")
    ? post.image
    : `https://tht-group.co.jp${post.image}`;

  const meta = {
    title: seoTitle,
    description: seoDescription,
    canonical,
    // title/description/canonical から og:title・og:description・og:url を自動生成
    auto: { ograph: true },
    meta: {
      charset: "utf-8",
      name: {
        keywords: [...commonBlogKeywords, ...(post.seo?.keywords ?? [])].join(","),
        "twitter:card": "summary_large_image",
      },
      property: {
        "og:type": "article",
        "og:site_name": "土日だけ体操教室",
        "og:image": absoluteImage,
      },
    },
  };

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: seoDescription,
    image: absoluteImage,
    datePublished: post.date,
    url: canonical,
    author: { "@type": "Organization", name: "土日だけ体操教室" },
    publisher: { "@type": "Organization", name: "土日だけ体操教室" },
    mainEntityOfPage: canonical,
  };

  const breadcrumbJson = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.item,
    })),
  };

  const faqItems = post.content
    .filter((b): b is Extract<typeof b, { type: "faq" }> => b.type === "faq")
    .flatMap((b) => b.items);
  const faqJson =
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }
      : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJson) }}
      />
      {faqJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJson) }}
        />
      )}
      <DocumentMeta {...meta}>
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Box sx={{ width: isMobile ? "90vw" : "60vw", m: "8vh 0" }}>
            <Typography
              sx={{
                color: "#EBB609",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
                mb: "20px",
              }}
              onClick={() => navigate(backHref)}
            >
              ＜ {backLabel}
            </Typography>

            <Box
              component="img"
              src={post.image}
              alt={thumbnailAlt}
              sx={{
                width: "100%",
                height: isMobile ? "200px" : "360px",
                objectFit: "cover",
                borderRadius: "16px",
                mb: "24px",
              }}
            />

            <Typography sx={{ color: "#999", fontSize: "13px", mb: "8px" }}>
              {post.date}
            </Typography>
            <Typography
              component="h1"
              variant={isMobile ? "h5" : "h4"}
              sx={{ color: "#333", fontWeight: 700, mb: "30px" }}
            >
              {post.title}
            </Typography>

            {post.content.map((block, i) => {
              switch (block.type) {
                case "heading":
                  return (
                    <Typography
                      key={i}
                      component="h2"
                      variant={isMobile ? "subtitle1" : "h6"}
                      sx={{
                        color: "#333",
                        fontWeight: 700,
                        borderLeft: "4px solid #EBB609",
                        pl: "12px",
                        mt: "40px",
                        mb: "16px",
                      }}
                    >
                      {block.text}
                    </Typography>
                  );
                case "note":
                  return (
                    <Typography
                      key={i}
                      sx={{
                        color: "#7a5b00",
                        bgcolor: "#FFF7DD",
                        fontSize: "13px",
                        fontWeight: 700,
                        borderRadius: "8px",
                        p: "10px 14px",
                        mb: "16px",
                        display: "inline-block",
                      }}
                    >
                      {block.text}
                    </Typography>
                  );
                case "link":
                  return (
                    <Typography
                      key={i}
                      sx={{
                        color: "#EBB609",
                        fontWeight: 700,
                        fontSize: "14px",
                        cursor: "pointer",
                        mb: "12px",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => navigate(block.href)}
                    >
                      {block.text} ＞
                    </Typography>
                  );
                case "cta":
                  return (
                    <Box key={i} sx={{ mb: "28px" }}>
                      <Button
                        variant="outlined"
                        sx={{
                          color: "#EBB609",
                          borderColor: "#EBB609",
                          borderRadius: "50px",
                          fontWeight: 700,
                          fontSize: "13px",
                          px: "24px",
                          py: "10px",
                          "&:hover": {
                            bgcolor: "#EBB609",
                            color: "#FFFFFF",
                            borderColor: "#EBB609",
                          },
                        }}
                        onClick={() => {
                          if (block.external) {
                            window.open(block.href, "_blank", "noopener,noreferrer");
                          } else {
                            navigate(block.href);
                          }
                        }}
                      >
                        {block.text}
                        {block.external ? "（外部サイト）" : ""}
                      </Button>
                    </Box>
                  );
                case "table":
                  return (
                    <Box key={i} sx={{ mb: "24px", overflowX: "auto" }}>
                      {block.caption && (
                        <Typography
                          sx={{
                            color: "#555",
                            fontSize: "13px",
                            fontWeight: 700,
                            mb: "8px",
                          }}
                        >
                          {block.caption}
                        </Typography>
                      )}
                      <Box
                        component="table"
                        sx={{
                          width: "100%",
                          minWidth: isMobile ? "480px" : "auto",
                          borderCollapse: "collapse",
                          fontSize: isMobile ? "13px" : "14px",
                        }}
                      >
                        <Box component="thead">
                          <Box component="tr">
                            {block.headers.map((h, hi) => (
                              <Box
                                key={hi}
                                component="th"
                                sx={{
                                  textAlign: "left",
                                  color: "#FFFFFF",
                                  bgcolor: "#EBB609",
                                  p: "8px 12px",
                                  fontWeight: 700,
                                }}
                              >
                                {h}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {block.rows.map((row, ri) => (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{
                                bgcolor: ri % 2 === 0 ? "#FFF" : "#FAFAFA",
                              }}
                            >
                              {row.map((cell, ci) => (
                                <Box
                                  key={ci}
                                  component="td"
                                  sx={{
                                    p: "8px 12px",
                                    color: "#333",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  {cell}
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                case "faq":
                  return (
                    <Box key={i} sx={{ mb: "24px" }}>
                      {block.items.map((item, qi) => (
                        <Box
                          key={qi}
                          sx={{
                            mb: "14px",
                            border: "1px solid #eee",
                            borderRadius: "8px",
                            p: "14px 16px",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#333",
                              fontWeight: 700,
                              fontSize: isMobile ? "14px" : "15px",
                              mb: "6px",
                            }}
                          >
                            Q. {item.q}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#555",
                              fontSize: isMobile ? "13px" : "14px",
                              lineHeight: 1.9,
                            }}
                          >
                            A. {item.a}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                case "paragraph":
                default:
                  return (
                    <Typography
                      key={i}
                      sx={{
                        color: "#333",
                        fontSize: isMobile ? "14px" : "16px",
                        lineHeight: 2,
                        mb: "20px",
                      }}
                    >
                      {block.text}
                    </Typography>
                  );
              }
            })}

            <Box sx={{ textAlign: "center", mt: "50px" }}>
              <Button
                sx={{
                  color: "#FFFFFF",
                  bgcolor: "darkgreen",
                  borderRadius: "50px",
                  p: "15px 40px",
                  fontSize: "16px",
                  "&:hover": { bgcolor: "#E0B24B" },
                }}
                onClick={() => navigate(backHref)}
              >
                {backLabel}
              </Button>
            </Box>
          </Box>
        </Grid>
      </DocumentMeta>
    </>
  );
};

export default BlogArticleView;
